import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { ssoConsumeToken, getMe, login, logout, mfaVerified } from './authSlice';

// auth.sentrivox.com directly, not cloud's own /api/v1/cloud/** backend -
// same pattern as SsoAuthCallback.jsx/Login.jsx's PKCE calls, since org
// switching is fundamentally an auth-service-issued-token operation (a new
// token pair scoped to the target org).
const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL || 'https://auth.sentrivox.com';

/**
 * Only ever succeeds for a user whose current token was issued by
 * auth-service (the "Continue with Sentrivox" SSO path) - a token from
 * cloud's own regular password login is signed with cloud's own legacy
 * secret, which auth-service cannot verify (by design - see
 * auth-service/routes/organizations.js's own comment on why it never holds
 * per-service legacy secrets). A 401 here is the correct, expected outcome
 * for a non-SSO session, not an error state - the switcher UI simply
 * doesn't render for those users (organizations stays empty).
 */
export const fetchMyOrganizations = createAsyncThunk(
  'organizations/fetchMine',
  async (_, { getState, rejectWithValue }) => {
    const token = getState().auth?.token;
    if (!token) return rejectWithValue('no_token');
    try {
      const res = await fetch(`${AUTH_SERVICE_URL}/organizations/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return rejectWithValue(`http_${res.status}`);
      const data = await res.json();
      return data.organizations || [];
    } catch (err) {
      return rejectWithValue(err.message || 'network_error');
    }
  }
);

export const switchOrganization = createAsyncThunk(
  'organizations/switch',
  async (organizationId, { getState, dispatch, rejectWithValue }) => {
    const token = getState().auth?.token;
    if (!token) return rejectWithValue('no_token');
    try {
      const res = await fetch(`${AUTH_SERVICE_URL}/organizations/switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ organization_id: organizationId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        return rejectWithValue(body.error || `http_${res.status}`);
      }
      const data = await res.json();
      // Same pattern as the SSO login callback: consume the new token, then
      // re-hydrate user state from it - everything downstream (role,
      // companyName, entitlement lookups keyed by tenant_id) must reflect
      // the NEW org, not a stale mix of old user doc + new token.
      dispatch(ssoConsumeToken(data.access_token));
      await dispatch(getMe()).unwrap();
      return data.organization_id;
    } catch (err) {
      return rejectWithValue(err.message || 'network_error');
    }
  }
);

const initialState = () => ({
  list: [], // {organization_id, company_name, role, is_primary}[]
  // currentOrgId is tracked here, not read off the user object - cloud's
  // own GET /api/auth/me response has no organization_id field (it wasn't
  // part of that endpoint's contract before today), so this slice is the
  // one source of truth for "which org is active right now" client-side.
  currentOrgId: null,
  loading: false,
  switching: false,
  error: null,
});

const organizationsSlice = createSlice({
  name: 'organizations',
  initialState: initialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyOrganizations.pending, (state) => { state.loading = true; })
      .addCase(fetchMyOrganizations.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
        if (!state.currentOrgId) {
          const primary = action.payload.find((o) => o.is_primary) || action.payload[0];
          state.currentOrgId = primary?.organization_id || null;
        }
      })
      .addCase(fetchMyOrganizations.rejected, (state) => {
        // Deliberately silent (see thunk doc) - a non-SSO session's failed
        // lookup is expected, not an error to surface to the user.
        state.loading = false;
        state.list = [];
      })
      .addCase(switchOrganization.pending, (state) => { state.switching = true; state.error = null; })
      .addCase(switchOrganization.fulfilled, (state, action) => {
        state.switching = false;
        state.currentOrgId = action.payload;
      })
      .addCase(switchOrganization.rejected, (state, action) => {
        state.switching = false;
        state.error = action.payload;
      })
      // The switch thunk itself dispatches ssoConsumeToken, so `switching` survives that reset.
      .addMatcher(
        isAnyOf(login.fulfilled, logout.pending, logout.fulfilled, getMe.rejected, mfaVerified, ssoConsumeToken),
        (state) => ({ ...initialState(), switching: state.switching })
      );
  },
});

export default organizationsSlice.reducer;
